/**
 * TOTP challenge UI — prompts for a 6-digit code to elevate the current
 * session to aal2 when the user already has a verified factor.
 */
import { useEffect, useRef, useState } from "react";
import { Loader2, AlertCircle, ShieldCheck } from "lucide-react";
import { listTotpFactors, verifyTotp } from "@/lib/auth/mfa";

const GRAD = "#5048E5";
const fg = "hsl(var(--foreground))";
const fgMuted = "hsl(var(--muted-foreground))";
const border = "hsl(var(--border))";
const muted = "hsl(var(--muted))";

export default function MfaChallenge({
  onVerified,
  onSignOut,
}: {
  onVerified: () => void;
  onSignOut?: () => void;
}) {
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    (async () => {
      try {
        const factors = await listTotpFactors();
        const verified = factors.find((f) => f.status === "verified");
        if (!verified) {
          setError("No authenticator found for this account.");
        } else {
          setFactorId(verified.id);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not load your authenticator.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (!factorId) return;
    setVerifying(true);
    setError("");
    try {
      await verifyTotp(factorId, code);
      onVerified();
    } catch {
      setError("That code didn't match. Check your authenticator app and try again.");
      setVerifying(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 size={22} className="animate-spin" style={{ color: GRAD }} />
      </div>
    );
  }

  return (
    <form onSubmit={handleVerify} className="flex flex-col gap-4">
      <p className="text-sm" style={{ color: fgMuted, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
        Enter the 6-digit code from your authenticator app to continue.
      </p>

      <input
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={6}
        autoFocus
        value={code}
        onChange={(e) => { setCode(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
        placeholder="123456"
        className="w-full rounded-xl px-4 py-3 text-center text-lg tracking-[0.3em] outline-none"
        style={{ background: muted, border: `1px solid ${border}`, color: fg, fontFamily: "monospace" }}
      />

      {error && (
        <p className="flex items-start gap-1.5 text-xs" style={{ color: "#ef4444", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
          <AlertCircle size={13} className="mt-px shrink-0" /> {error}
        </p>
      )}

      <button type="submit" disabled={verifying || code.length !== 6 || !factorId}
        className="flex items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition-all hover:opacity-90 disabled:opacity-50"
        style={{ background: GRAD, color: "#fff", fontFamily: "Plus Jakarta Sans, sans-serif" }}>
        {verifying ? <Loader2 size={15} className="animate-spin" /> : <ShieldCheck size={15} />}
        {verifying ? "Verifying…" : "Verify"}
      </button>

      {onSignOut && (
        <button type="button" onClick={onSignOut} disabled={verifying}
          className="rounded-xl py-2.5 text-sm font-semibold transition-all hover:opacity-90 disabled:opacity-60"
          style={{ background: muted, border: `1px solid ${border}`, color: fg, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
          Sign out
        </button>
      )}
    </form>
  );
}
